import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircle2, Circle, ChevronRight, Loader2, PartyPopper, FolderTree, Users, Wallet, Bot, Palette,
  Briefcase, UserCog, CalendarCheck, CalendarDays, ArrowLeft,
} from 'lucide-react';
import { useSetupProgress, SetupStep } from './useSetupProgress';

const ICONS: Record<string, React.ReactNode> = {
  departments: <FolderTree className="w-5 h-5" />,
  team: <Users className="w-5 h-5" />,
  job_titles: <Briefcase className="w-5 h-5" />,
  roles: <UserCog className="w-5 h-5" />,
  leave_policies: <CalendarCheck className="w-5 h-5" />,
  holidays: <CalendarDays className="w-5 h-5" />,
  payroll: <Wallet className="w-5 h-5" />,
  ai: <Bot className="w-5 h-5" />,
  branding: <Palette className="w-5 h-5" />,
};

export const SetupChecklistPage: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const { tenantSlug } = useParams();
  const path = (p: string) => (tenantSlug ? `/t/${tenantSlug}/app/${p}` : `/app/${p}`);
  const { steps, loading, doneCount, total, allRequiredDone, allDone } = useSetupProgress();

  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  // Overall status: drives the badge + completion hero. "All set" is the terminal
  // state once every step (including the optional ones) is done.
  const status: 'loading' | 'in_progress' | 'ready' | 'all_set' =
    loading ? 'loading' : allDone ? 'all_set' : allRequiredDone ? 'ready' : 'in_progress';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Link to={path('dashboard')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="w-4 h-4" /> {t('setup.back_to_dashboard', 'Back to dashboard')}
      </Link>

      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-h2 text-charcoal">{t('setup.title')}</h1>
          {status === 'all_set' && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-success-soft text-success-strong">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t('setup.badge_all_set')}
            </span>
          )}
          {status === 'ready' && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-500/[0.16] dark:text-orange-300">
              {t('setup.badge_ready')}
            </span>
          )}
        </div>
        <p className="text-muted-foreground mt-1">{t('setup.subtitle')}</p>
      </div>

      {/* Completion hero — shown once everything is done */}
      {status === 'all_set' ? (
        <div className="rounded-2xl border border-success bg-success-soft p-5">
          <div className="flex items-start gap-3">
            <PartyPopper className="w-6 h-6 text-success shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-success-strong">{t('setup.all_set_title')}</div>
              <p className="text-sm text-success-strong mt-0.5">{t('setup.all_set_desc')}</p>
            </div>
          </div>
        </div>
      ) : (
        /* Progress */
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              {loading ? t('setup.checking') : t('setup.progress', { done: doneCount, total })}
            </span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          {allRequiredDone && !loading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-success">
              <PartyPopper className="w-4 h-4" /> {t('setup.essentials_done')}
            </div>
          )}
        </div>
      )}

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((s) => (
          <StepRow key={s.key} step={s} to={`${path(s.pathSuffix)}?from=setup`} icon={ICONS[s.key]} loading={loading} t={t} />
        ))}
      </div>
    </div>
  );
};

const StepRow: React.FC<{ step: SetupStep; to: string; icon: React.ReactNode; loading: boolean; t: (k: string, d?: any) => string }> = ({
  step,
  to,
  icon,
  loading,
  t,
}) => (
  <Link
    to={to}
    className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 hover:border-orange-300 dark:hover:border-orange-500/40 transition-colors"
  >
    <span className="shrink-0">
      {loading ? (
        <Circle className="w-6 h-6 text-muted-foreground/40" />
      ) : step.done ? (
        <CheckCircle2 className="w-6 h-6 text-success" />
      ) : (
        <Circle className="w-6 h-6 text-muted-foreground/50" />
      )}
    </span>

    <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
      {icon}
    </span>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className={`font-semibold ${step.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
          {t(`setup.steps.${step.key}.title`, step.title)}
        </span>
        {!step.required && (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{t('setup.optional')}</span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-0.5">{t(`setup.steps.${step.key}.description`, step.description)}</p>
    </div>

    {step.done ? (
      <span className="text-sm text-success shrink-0">{t('setup.done')}</span>
    ) : (
      <span className="text-sm text-orange-600 inline-flex items-center gap-1 shrink-0">
        {t(`setup.steps.${step.key}.cta`, step.cta)} <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    )}
  </Link>
);

export default SetupChecklistPage;
